// The admin panel (#371): people, codes, usage, shared setlists, tune
// defaults, new tunes and the library — plain client code running as the
// signed-in admin under the 0005-0009 RLS policies. No service key, no
// separate backend: a non-admin reaching this route sees a refusal, and a
// non-admin who somehow rendered the dashboard would get empty lists and
// refused writes from the server — the role gate here is presentation, the
// database is the lock.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { isAppleRelayEmail, personLabel } from '@/auth/identity';
import { useAuth, type PilotRole } from '@/auth/session';
import { AccountSection } from '@/components/AccountSection';
import {
  fetchUserTune,
  listAllSetlists,
  listCodes,
  listEvents,
  listLibrary,
  listProfiles,
  listUserTunes,
  listUsers,
  inviteLinkUrl,
  mintCodes,
  mintInviteLink,
  promoteUserTune,
  removeLibraryTune,
  setCodeRevoked,
  summariseUsage,
  unshareSetlist,
  updateProfile,
  type AdminCode,
  type AdminEvent,
  type AdminLibraryTune,
  type AdminProfile,
  type AdminSetlist,
  type AdminUser,
  type AdminUserTune,
  type UserUsage,
} from '@/data/admin';
import { refreshLibraryTunes } from '@/data/libraryTunes';
import { DrumLoopsCard } from '@/pages/AdminDrumLoops';
import { FeedbackCard } from '@/pages/AdminFeedback';
import { setlistShareUrl } from '@/data/sharedSetlists';
import { fetchTuneDefaults, removeTuneDefault, type TuneDefaultRow } from '@/data/tuneDefaults';
import { describeBandPreset } from '@/music/style';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import type { HouseSettings } from '@/state/tunePlayback';

const ROLES: PilotRole[] = ['user', 'admin'];

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

export function AdminPage() {
  const auth = useAuth();

  if (auth.status === 'unconfigured') {
    return (
      <div className="admin-page" data-testid="admin-page">
        <h1>Admin</h1>
        <p className="muted">Not available in this build.</p>
      </div>
    );
  }
  if (auth.status !== 'active') {
    return (
      <div className="admin-page" data-testid="admin-page">
        <h1>Admin</h1>
        <div className="admin-signin" data-testid="admin-signin">
          <p className="muted">Sign in with an admin account.</p>
          <AccountSection />
        </div>
      </div>
    );
  }
  if (auth.role !== 'admin') {
    return (
      <div className="admin-page" data-testid="admin-page">
        <h1>Admin</h1>
        <p className="muted" data-testid="admin-denied">
          This page is for the pilot admin.
        </p>
      </div>
    );
  }
  return <AdminDashboard ownEmail={auth.email} />;
}

function AdminDashboard({ ownEmail }: { ownEmail: string | null }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [codes, setCodes] = useState<AdminCode[]>([]);
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [setlists, setSetlists] = useState<AdminSetlist[]>([]);
  const [defaults, setDefaults] = useState<TuneDefaultRow[]>([]);
  const [userTunes, setUserTunes] = useState<AdminUserTune[]>([]);
  const [library, setLibrary] = useState<AdminLibraryTune[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setErrors([]);
    const fail = (card: string) => (e: unknown) => {
      if (!cancelled) {
        setErrors((prev) => [...prev, `${card}: ${e instanceof Error ? e.message : 'failed'}`]);
      }
      return null;
    };
    void Promise.all([
      listUsers().catch(fail('People')),
      listProfiles().catch(fail('People')),
      listCodes().catch(fail('Codes')),
      listEvents().catch(fail('Usage')),
      listAllSetlists().catch(fail('Shared playlists')),
      fetchTuneDefaults().catch(fail('Tune defaults')),
      listUserTunes().catch(fail('New tunes')),
      listLibrary().catch(fail('Library')),
    ]).then(([u, p, c, e, s, d, t, l]) => {
      if (cancelled) return;
      if (u) setUsers(u);
      if (p) setProfiles(p);
      if (c) setCodes(c);
      if (e) setEvents(e);
      if (s) setSetlists(s);
      if (d) setDefaults(d);
      if (t) setUserTunes(t);
      if (l) setLibrary(l);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const usage = useMemo(() => summariseUsage(events), [events]);
  const emailOf = useMemo(() => {
    const map = new Map(users.map((u) => [u.userId, personLabel(u.email, u.name, u.userId)]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [users]);

  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-head">
        <h1>Admin</h1>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="admin-refresh"
          onClick={() => setReload((n) => n + 1)}
        >
          Refresh
        </button>
      </header>

      {errors.map((e) => (
        <p key={e} className="home-load-error" role="alert">
          {e}
        </p>
      ))}
      {!loaded && <p className="muted">Loading…</p>}

      {loaded && (
        <>
          <FeedbackCard emailOf={emailOf} reload={reload} />
          <PeopleCard
            users={users}
            profiles={profiles}
            usage={usage.perUser}
            ownEmail={ownEmail}
            onChanged={setProfiles}
          />
          <UsageCard usage={usage} emailOf={emailOf} />
          <CodesCard codes={codes} onChanged={setCodes} />
          <SetlistsCard setlists={setlists} emailOf={emailOf} onChanged={setSetlists} />
          <TuneDefaultsCard defaults={defaults} emailOf={emailOf} onChanged={setDefaults} />
          <NewTunesCard
            tunes={userTunes}
            library={library}
            emailOf={emailOf}
            onPromoted={(entry) => setLibrary((current) => [entry, ...current])}
          />
          <LibraryCard library={library} emailOf={emailOf} onChanged={setLibrary} />
          <DrumLoopsCard />
        </>
      )}
    </div>
  );
}

// --- People -------------------------------------------------------------------

function PeopleCard({
  users,
  profiles,
  usage,
  ownEmail,
  onChanged,
}: {
  users: AdminUser[];
  profiles: AdminProfile[];
  usage: UserUsage[];
  ownEmail: string | null;
  onChanged: (update: (profiles: AdminProfile[]) => AdminProfile[]) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const profileOf = new Map(profiles.map((p) => [p.userId, p]));
  const usageOf = new Map(usage.map((u) => [u.userId, u]));

  const patch = async (
    userId: string,
    changes: Partial<Pick<AdminProfile, 'role' | 'hasAccess'>>,
  ) => {
    setError(null);
    const { hasAccess, ...rest } = changes;
    try {
      await updateProfile(userId, {
        ...rest,
        ...(hasAccess === undefined ? {} : { has_access: hasAccess }),
      });
      // Applied to the list as it is NOW: another change may have landed
      // while this one was in flight.
      onChanged((current) => current.map((p) => (p.userId === userId ? { ...p, ...changes } : p)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    }
  };

  return (
    <section className="admin-card" data-testid="admin-people">
      <h2>People</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Access</th>
            <th>Batch</th>
            <th>Last seen</th>
            <th>Play minutes</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const profile = profileOf.get(user.userId);
            const use = usageOf.get(user.userId);
            const self = ownEmail !== null && user.email === ownEmail;
            const admin = profile?.role === 'admin';
            return (
              <tr key={user.userId}>
                <td title={user.email ?? undefined}>
                  {personLabel(user.email, user.name, user.userId)}
                  {user.name && user.email && !isAppleRelayEmail(user.email) && (
                    <div className="muted">{user.name}</div>
                  )}
                </td>
                <td>
                  <select
                    aria-label={`Role for ${user.email ?? user.userId}`}
                    value={profile?.role ?? 'user'}
                    disabled={self || !profile}
                    onChange={(e) => void patch(user.userId, { role: e.target.value as PilotRole })}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {/* An admin always has access, and nobody locks themselves out. */}
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-pressed={admin || !!profile?.hasAccess}
                    disabled={self || admin || !profile}
                    aria-label={`Access for ${user.email ?? user.userId}`}
                    onClick={() => void patch(user.userId, { hasAccess: !profile?.hasAccess })}
                  >
                    {admin || profile?.hasAccess ? 'On' : 'Off'}
                  </button>
                </td>
                <td>{profile?.batch ?? '—'}</td>
                <td>{day(use?.lastSeen ?? user.lastSignIn)}</td>
                <td>{use ? use.playMinutes : 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// --- Usage --------------------------------------------------------------------

function UsageCard({
  usage,
  emailOf,
}: {
  usage: ReturnType<typeof summariseUsage>;
  emailOf: (id: string) => string;
}) {
  return (
    <section className="admin-card" data-testid="admin-usage">
      <h2>Usage</h2>
      <div className="admin-tiles">
        <div className="admin-tile">
          <strong>{usage.totalPlayMinutes}</strong>
          <span className="muted">play minutes</span>
        </div>
        <div className="admin-tile">
          <strong>{usage.activeLastWeek}</strong>
          <span className="muted">active this week</span>
        </div>
        <div className="admin-tile">
          <strong>{usage.perUser.filter((u) => u.returnedAfterWeek).length}</strong>
          <span className="muted">came back after a week</span>
        </div>
      </div>
      <div className="admin-lists">
        <div>
          <h3>Minutes per person</h3>
          {usage.perUser.length === 0 && <p className="muted">No events yet.</p>}
          <ul>
            {usage.perUser.map((u) => (
              <li key={u.userId}>
                {emailOf(u.userId)} <span className="muted">{u.playMinutes} min</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3>Most opened tunes</h3>
          <ul>
            {usage.topTunes.map((t) => (
              <li key={t.title}>
                {t.title} <span className="muted">{t.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3>Features used</h3>
          <ul>
            {usage.features.map((f) => (
              <li key={f.feature}>
                {f.feature} <span className="muted">{f.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// --- Codes --------------------------------------------------------------------

function linkExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'no expiry';
  const at = new Date(expiresAt);
  if (at.getTime() <= Date.now()) return 'expired';
  return `until ${at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}

function CodesCard({
  codes,
  onChanged,
}: {
  codes: AdminCode[];
  onChanged: (update: (codes: AdminCode[]) => AdminCode[]) => void;
}) {
  // One code for one person, or a batch for a class. Every code is single-use
  // either way; a one-off is labelled with who it is for (its batch label, so
  // the People card shows it too) and falls back to "one-off". An invite link
  // (0016) is one address for many people, sent as /join/<token>.
  const [mode, setMode] = useState<'one' | 'batch' | 'link'>('one');
  const [who, setWho] = useState('');
  const [batch, setBatch] = useState('');
  const [count, setCount] = useState(5);
  const [people, setPeople] = useState(30);
  const [lasts, setLasts] = useState<number | null>(14);
  const [minted, setMinted] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [copiedRow, setCopiedRow] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = mode === 'one' ? who.trim() || 'one-off' : batch.trim();
  const [mintedLink, setMintedLink] = useState<string | null>(null);

  const mint = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      if (mode === 'link') {
        const token = await mintInviteLink({
          batch: label,
          maxUses: people,
          expiresInDays: lasts,
        });
        setMinted([]);
        setMintedLink(inviteLinkUrl(token));
        onChanged((current) => [
          ...current,
          {
            code: token,
            batch: label,
            useCount: 0,
            maxUses: people,
            revoked: false,
            kind: 'link',
            expiresAt:
              lasts === null ? null : new Date(Date.now() + lasts * 86_400_000).toISOString(),
          },
        ]);
        return;
      }
      const fresh = await mintCodes({
        batch: label,
        count: mode === 'one' ? 1 : count,
      });
      setMinted(fresh);
      setMintedLink(null);
      if (mode === 'one') setWho('');
      onChanged((current) => [
        ...current,
        ...fresh.map((code) => ({
          code,
          batch: label,
          useCount: 0,
          maxUses: 1,
          revoked: false,
          kind: 'code' as const,
          expiresAt: null,
        })),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Minting was refused.');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text: string, mark: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(mark === '');
      setCopiedRow(mark || null);
      setTimeout(() => {
        setCopied(false);
        setCopiedRow(null);
      }, 2000);
    } catch {
      // Clipboard refused: the codes are on screen and selectable.
    }
  };
  const copyMinted = () => copyText(mintedLink ?? minted.join('\n'), '');

  const revoke = async (code: AdminCode) => {
    setError(null);
    try {
      const revoked = !code.revoked;
      await setCodeRevoked(code.code, revoked);
      onChanged((current) => current.map((c) => (c.code === code.code ? { ...c, revoked } : c)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    }
  };

  return (
    <section className="admin-card" data-testid="admin-codes">
      <h2>Codes</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      <div className="segmented admin-mint-mode" role="group" aria-label="Mint">
        {(
          [
            ['one', 'One code'],
            ['batch', 'Batch'],
            ['link', 'Invite link'],
          ] as const
        ).map(([id, text]) => (
          <button
            key={id}
            type="button"
            className={mode === id ? 'active' : ''}
            aria-pressed={mode === id}
            data-testid={`admin-mint-mode-${id}`}
            onClick={() => setMode(id)}
          >
            {text}
          </button>
        ))}
      </div>
      <div className="admin-mint">
        {mode === 'one' ? (
          <input
            className="text-input"
            data-testid="admin-mint-who"
            aria-label="Who the code is for"
            placeholder="Who it's for"
            maxLength={40}
            value={who}
            onChange={(e) => setWho(e.target.value)}
          />
        ) : mode === 'link' ? (
          <>
            <input
              className="text-input"
              data-testid="admin-mint-link-label"
              aria-label="Link label"
              placeholder="Label"
              maxLength={40}
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
            />
            <input
              type="number"
              className="text-input narrow admin-mint-count"
              data-testid="admin-mint-people"
              aria-label="How many people"
              min={1}
              max={500}
              value={people}
              onChange={(e) => setPeople(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            />
            <select
              className="text-input"
              data-testid="admin-mint-lasts"
              aria-label="Lasts"
              value={lasts === null ? 'never' : String(lasts)}
              onChange={(e) => setLasts(e.target.value === 'never' ? null : Number(e.target.value))}
            >
              <option value="7">1 week</option>
              <option value="14">2 weeks</option>
              <option value="30">1 month</option>
              <option value="never">No expiry</option>
            </select>
          </>
        ) : (
          <>
            <input
              className="text-input"
              data-testid="admin-mint-batch"
              aria-label="Batch name"
              placeholder="Batch"
              maxLength={40}
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
            />
            <input
              type="number"
              className="text-input narrow admin-mint-count"
              data-testid="admin-mint-count"
              aria-label="How many codes"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </>
        )}
        <button
          type="button"
          className="btn btn-sm btn-primary"
          data-testid="admin-mint"
          disabled={busy || !label}
          onClick={() => void mint()}
        >
          Mint
        </button>
      </div>
      {(minted.length > 0 || mintedLink) && (
        <div className="admin-minted-row">
          <p className="admin-minted" data-testid="admin-minted">
            {mintedLink ?? minted.join(' · ')}
          </p>
          <button
            type="button"
            className="btn btn-sm"
            data-testid="admin-minted-copy"
            onClick={() => void copyMinted()}
          >
            <Copy size={14} aria-hidden="true" /> {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Batch</th>
            <th>Used</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code} className={c.revoked ? 'is-revoked' : undefined}>
              <td className="admin-code">
                {c.kind === 'link' ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    data-testid="admin-link-copy"
                    aria-label={`Copy invite link for ${c.batch}`}
                    onClick={() => void copyText(inviteLinkUrl(c.code), c.code)}
                  >
                    <Copy size={14} aria-hidden="true" />{' '}
                    {copiedRow === c.code ? 'Copied' : 'Copy link'}
                  </button>
                ) : (
                  c.code
                )}
              </td>
              <td>{c.batch}</td>
              <td>
                {c.useCount}/{c.maxUses}
                {c.kind === 'link' && <span className="muted"> · {linkExpiry(c.expiresAt)}</span>}
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  aria-label={`${c.revoked ? 'Restore' : 'Revoke'} ${c.code}`}
                  onClick={() => void revoke(c)}
                >
                  {c.revoked ? 'Restore' : 'Revoke'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// --- Setlists -----------------------------------------------------------------

function SetlistsCard({
  setlists,
  emailOf,
  onChanged,
}: {
  setlists: AdminSetlist[];
  emailOf: (id: string) => string;
  onChanged: (update: (setlists: AdminSetlist[]) => AdminSetlist[]) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const unshare = async (shareId: string) => {
    setError(null);
    try {
      await unshareSetlist(shareId);
      onChanged((current) => current.filter((s) => s.shareId !== shareId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setArmed(null);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-setlists">
      <h2>Shared playlists</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      {setlists.length === 0 ? (
        <p className="muted">Nothing shared yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Shared by</th>
              <th>Updated</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {setlists.map((s) => (
              <tr key={s.shareId}>
                <td>{s.name}</td>
                <td>{emailOf(s.owner)}</td>
                <td>{day(s.updatedAt)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    aria-label={`Copy the link for ${s.name}`}
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(setlistShareUrl(s.shareId))
                        .catch(() => undefined);
                    }}
                  >
                    Copy link
                  </button>
                </td>
                <td>
                  {armed === s.shareId ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      data-testid="admin-unshare-confirm"
                      onBlur={() => setArmed(null)}
                      onClick={() => void unshare(s.shareId)}
                    >
                      Unshare?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Unshare ${s.name}`}
                      onClick={() => setArmed(s.shareId)}
                    >
                      Unshare
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// --- Tune defaults -------------------------------------------------------------

/** One line of what a default sets: tempo, key, choruses and the band's groove. */
function describeHouse(settings: HouseSettings): string {
  const parts: string[] = [];
  if (settings.tempo) parts.push(`${settings.tempo} bpm`);
  if (settings.key) parts.push(settings.key.replace('b', '♭').replace('#', '♯'));
  if (settings.repeats) parts.push(`×${settings.repeats}`);
  if (settings.band) parts.push(describeBandPreset(settings.band)?.label ?? 'Custom band');
  return parts.join(' · ');
}

function tuneLink(title: string, composer: string, source?: 'library'): string {
  return `${APP_ROUTES.workbench}?tune=${encodeURIComponent(title)}${
    composer ? `&by=${encodeURIComponent(composer)}` : ''
  }${source ? `&source=${source}` : ''}`;
}

function TuneDefaultsCard({
  defaults,
  emailOf,
  onChanged,
}: {
  defaults: TuneDefaultRow[];
  emailOf: (id: string) => string;
  onChanged: (update: (defaults: TuneDefaultRow[]) => TuneDefaultRow[]) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const drop = useStore((s) => s.dropHouseDefault);

  const remove = async (tuneKey: string) => {
    setError(null);
    try {
      await removeTuneDefault(tuneKey);
      drop(tuneKey);
      onChanged((current) => current.filter((d) => d.tuneKey !== tuneKey));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setArmed(null);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-tune-defaults">
      <h2>Tune defaults</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      {defaults.length === 0 ? (
        <p className="muted">No defaults saved yet. Save one from a tune’s details.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tune</th>
              <th>Sets</th>
              <th>Saved by</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {defaults.map((d) => (
              <tr key={d.tuneKey}>
                <td>
                  <Link to={tuneLink(d.title, d.composer)}>{d.title || d.tuneKey}</Link>
                  {d.composer && <span className="muted"> · {d.composer}</span>}
                </td>
                <td>{describeHouse(d.settings)}</td>
                <td>{d.updatedBy ? emailOf(d.updatedBy) : '—'}</td>
                <td>{day(d.updatedAt)}</td>
                <td>
                  {armed === d.tuneKey ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      data-testid="admin-default-remove-confirm"
                      onBlur={() => setArmed(null)}
                      onClick={() => void remove(d.tuneKey)}
                    >
                      Remove?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Remove the default for ${d.title || d.tuneKey}`}
                      onClick={() => setArmed(d.tuneKey)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// --- New tunes ------------------------------------------------------------------

const tuneRowKey = (owner: string | null, tuneId: string | null) => `${owner}/${tuneId}`;

/** What people have saved to their accounts, newest first: open one here, or
 *  copy it into the library for everyone. */
function NewTunesCard({
  tunes,
  library,
  emailOf,
  onPromoted,
}: {
  tunes: AdminUserTune[];
  library: AdminLibraryTune[];
  emailOf: (id: string) => string;
  onPromoted: (entry: AdminLibraryTune) => void;
}) {
  const navigate = useNavigate();
  const loadScore = useStore((s) => s.loadScore);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: '', composer: '', credit: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inLibrary = new Set(library.map((l) => tuneRowKey(l.sourceOwner, l.sourceTuneId)));

  const open = async (tune: AdminUserTune) => {
    setError(null);
    try {
      loadScore(await fetchUserTune(tune.owner, tune.tuneId));
      navigate(APP_ROUTES.workbench);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That tune could not be opened.');
    }
  };

  const startPromote = (tune: AdminUserTune) => {
    setError(null);
    setDraft({ title: tune.title, composer: tune.composer, credit: '' });
    setPromoting(tuneRowKey(tune.owner, tune.tuneId));
  };

  const promote = async (tune: AdminUserTune) => {
    setBusy(true);
    setError(null);
    try {
      const id = await promoteUserTune({ owner: tune.owner, tuneId: tune.tuneId, ...draft });
      onPromoted({
        id,
        title: draft.title.trim() || tune.title,
        composer: draft.composer.trim(),
        credit: draft.credit.trim(),
        createdAt: new Date().toISOString(),
        sourceOwner: tune.owner,
        sourceTuneId: tune.tuneId,
      });
      setPromoting(null);
      void refreshLibraryTunes();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The tune was not added.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-new-tunes">
      <h2>New tunes</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      {tunes.length === 0 ? (
        <p className="muted">No tunes saved to accounts yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tune</th>
              <th>Saved by</th>
              <th>Saved</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tunes.map((tune) => {
              const key = tuneRowKey(tune.owner, tune.tuneId);
              const name = tune.title || 'Untitled';
              return [
                <tr key={key}>
                  <td>
                    {name}
                    {tune.composer && <span className="muted"> · {tune.composer}</span>}
                  </td>
                  <td>{emailOf(tune.owner)}</td>
                  <td>{day(tune.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Open ${name}`}
                      onClick={() => void open(tune)}
                    >
                      Open
                    </button>
                  </td>
                  <td>
                    {inLibrary.has(key) ? (
                      <span className="muted">In library</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        aria-label={`Promote ${name}`}
                        aria-expanded={promoting === key}
                        onClick={() =>
                          promoting === key ? setPromoting(null) : startPromote(tune)
                        }
                      >
                        Promote
                      </button>
                    )}
                  </td>
                </tr>,
                promoting === key && (
                  <tr key={`${key}-promote`} className="admin-promote-row">
                    <td colSpan={5}>
                      <form
                        className="admin-promote"
                        data-testid="admin-promote-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          void promote(tune);
                        }}
                      >
                        <input
                          className="text-input"
                          aria-label="Title"
                          placeholder="Title"
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                        <input
                          className="text-input"
                          aria-label="Composer"
                          placeholder="Composer"
                          value={draft.composer}
                          onChange={(e) => setDraft({ ...draft, composer: e.target.value })}
                        />
                        <input
                          className="text-input"
                          aria-label="Credit"
                          placeholder="Credit"
                          maxLength={120}
                          value={draft.credit}
                          onChange={(e) => setDraft({ ...draft, credit: e.target.value })}
                        />
                        <button
                          type="submit"
                          className="btn btn-sm btn-primary"
                          data-testid="admin-promote-confirm"
                          disabled={busy}
                        >
                          Add to library
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => setPromoting(null)}
                        >
                          Cancel
                        </button>
                      </form>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// --- Library --------------------------------------------------------------------

function LibraryCard({
  library,
  emailOf,
  onChanged,
}: {
  library: AdminLibraryTune[];
  emailOf: (id: string) => string;
  onChanged: (update: (library: AdminLibraryTune[]) => AdminLibraryTune[]) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = async (id: string) => {
    setError(null);
    try {
      await removeLibraryTune(id);
      onChanged((current) => current.filter((l) => l.id !== id));
      void refreshLibraryTunes();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setArmed(null);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-library">
      <h2>Library</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      {library.length === 0 ? (
        <p className="muted">Nothing in the library yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tune</th>
              <th>Credit</th>
              <th>From</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {library.map((l) => (
              <tr key={l.id}>
                <td>
                  <Link to={tuneLink(l.title, l.composer, 'library')}>{l.title}</Link>
                  {l.composer && <span className="muted"> · {l.composer}</span>}
                </td>
                <td>{l.credit || '—'}</td>
                <td>{l.sourceOwner ? emailOf(l.sourceOwner) : '—'}</td>
                <td>{day(l.createdAt)}</td>
                <td>
                  {armed === l.id ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      data-testid="admin-library-remove-confirm"
                      onBlur={() => setArmed(null)}
                      onClick={() => void remove(l.id)}
                    >
                      Remove?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Remove ${l.title} from the library`}
                      onClick={() => setArmed(l.id)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
